/**
 * Feedback to the user — "Profile saved", "Save failed" — lives in the page,
 * not in a popup card in the corner. This store holds what was said; the
 * NoticeBar component shows it at the top of the page's content column.
 *
 * Lifetimes: a success or info notice goes after a few seconds; a warning
 * gets longer; an error stays until the user dismisses it. On navigation,
 * anything the user has had time to read is dropped, but a notice raised a
 * moment before the navigation (`toast.success("Saved"); navigate("/jobs")`)
 * follows them to the new page — otherwise it would vanish unread.
 *
 * `toast` mirrors the sonner API the pages were written against, so the
 * move from the popup was an import swap, not a rewrite of 170 call sites.
 */
import { useSyncExternalStore } from "react";

const DEFAULT_MS = { success: 6000, info: 6000, warning: 10000, error: null };
/** A notice younger than this survives a route change (raised on the way out). */
const FRESH_MS = 1500;

let snapshot = { notices: [], primaryMounts: 0 };
const listeners = new Set();
const timers = new Map();
let seq = 0;

function emit(next) {
  snapshot = next;
  listeners.forEach((fn) => fn());
}

export function getSnapshot() { return snapshot; }

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function useNotices() {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export function notify(kind, message, opts = {}) {
  const id = `n${++seq}`;
  const notice = {
    id, kind, message: String(message ?? ""),
    description: opts.description, action: opts.action,
    raisedAt: Date.now(),
  };
  emit({ ...snapshot, notices: [...snapshot.notices, notice] });
  // `null` is a lifetime too (error: stays), so this is not a `??` chain.
  const ms = opts.duration !== undefined ? opts.duration
    : (kind in DEFAULT_MS ? DEFAULT_MS[kind] : DEFAULT_MS.info);
  if (ms) timers.set(id, setTimeout(() => dismiss(id), ms));
  return id;
}

export function dismiss(id) {
  const t = timers.get(id);
  if (t) { clearTimeout(t); timers.delete(id); }
  if (!snapshot.notices.some((n) => n.id === id)) return;
  emit({ ...snapshot, notices: snapshot.notices.filter((n) => n.id !== id) });
}

export function clear() {
  timers.forEach((t) => clearTimeout(t));
  timers.clear();
  if (snapshot.notices.length) emit({ ...snapshot, notices: [] });
}

/** Called by the bar when the route changes: keep only what was just raised. */
export function onRouteChange(now = Date.now()) {
  const stale = snapshot.notices.filter((n) => now - n.raisedAt > FRESH_MS);
  stale.forEach((n) => dismiss(n.id));
}

/** A page-level bar registers so the app-root fallback can step aside. */
export function registerPrimaryMount() {
  emit({ ...snapshot, primaryMounts: snapshot.primaryMounts + 1 });
  return () => emit({ ...snapshot, primaryMounts: snapshot.primaryMounts - 1 });
}

export const toast = {
  success: (message, opts) => notify("success", message, opts),
  error: (message, opts) => notify("error", message, opts),
  info: (message, opts) => notify("info", message, opts),
  warning: (message, opts) => notify("warning", message, opts),
  message: (message, opts) => notify("info", message, opts),
  dismiss: (id) => (id ? dismiss(id) : clear()),
};
